import { useContext } from 'react'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useBrand from '@hooks/useBrand'
import useChartData from '@hooks/useChartData'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'

import PageLayout from '../../../../components/PageLayout'

import AgenciesRelationsTable from '../AgenciesRelationsTable'
import BrandInactiveBanner from '../../../../components/BrandInactiveBanner'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        style,
        entityKey: 'brandThemes',
        endpoint: 'brand_themes',
      },
      key: 'brand_theme_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'sortKey',
      label: 'Sort by',
      values: [
        { key: 'agents_count', label: 'Agents' },
        { key: 'api_keys_count', label: 'API Keys' },
        { key: 'brand_groups_count', label: 'Brand Groups' },
        { key: 'campaigns_count', label: 'Campaigns' },
        { key: 'id', label: 'ID' },
        { key: 'image_libraries_count', label: 'Image Libraries' },
        { key: 'office_administrators_count', label: 'Admins' },
        { key: 'portals_count', label: 'Portals' },
        { key: 'provider_packages_count', label: 'Packages' },
        { key: 'providers_count', label: 'Providers' },
        { key: 'publications', label: 'Campaigns' },
        { key: 'teams_count', label: 'Teams' },
        { key: 'template_pages_count', label: 'Templates' },
        { key: 'title', label: 'Title' },
      ],
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'sortDirection',
      label: 'Sort Direction',
      values: [
        { key: 'asc', label: 'Ascending' },
        { key: 'desc', label: 'Descending' },
      ],
    },
  ]
}

interface PageContentProps {
  brandId: number,
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { brandId, pageFilters } = props

  const brandPayload = useBrand({ id: brandId })
  const { brand } = brandPayload

  const { sortKey, sortDirection, ...restFilters } = pageFilters

  const chartDataPayload = useChartData('brand_agencies_overview', {
    entityId: brandId,
    sort: { sortKey, sortDirection },
    requestOptions: { brand_id: brandId, ...restFilters },
    performHttpRequests: !!brandId,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredDataArray,
    filteredDataArrayCount,
    loading,
  } = chartDataPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: { resetFilters, updateFilters },
    defaultFilters,
  } = pageContext

  return (
    <PageLayout activeTabBarItemKey="agenciesOverview" entityPayload={brandPayload}>
      <Columns flexDirection={['column', 'column', 'row']}>
        <Columns.Main>
          <LoadingModule loading={loading} times={3} />

          {brand.hidden && (
            <BrandInactiveBanner brandPayload={brandPayload} />
          )}

          {!loading && !filteredDataArrayCount && <ListBlankState />}

          {!loading && !!filteredDataArrayCount && (
            <DashboardModule title="Overview">
              <Box flexDirection="column">
                {/*
                 // @ts-expect-error TS2322 */}
                <AgenciesRelationsTable brandId={brandId} dataPayload={filteredDataArray} />

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredDataArrayCount}
                  loading={loading}
                />
              </Box>
            </DashboardModule>
          )}
        </Columns.Main>

        <Columns.Sidebar>
          <PageFilterModule
            callbacks={{ resetFilters, updateFilters }}
            defaultFilters={defaultFilters}
            // @ts-expect-error TS2322
            filterFields={buildPageFilterFields()}
            pageFilters={pageFilters}
            title="Filters"
          />
        </Columns.Sidebar>
      </Columns>
    </PageLayout>
  )
}

export default PageContent
